import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';
import { SparklesIcon, CloseIcon } from './Icons';
import { VOICE_PRESETS, VoicePreset } from '../utils/voiceChangerEngine';

export interface VoiceChangerModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentPreset: VoicePreset;
    onSelectPreset: (preset: VoicePreset) => void;
    onTransformElevenLabs?: (voiceId: string, apiKey?: string) => void;
    isConvertingElevenLabs?: boolean;
}

export const ELEVENLABS_VOICES = [
    { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel', gender: 'Female', desc: 'Calm & Professional' },
    { id: 'AZnzlk1XvdvUeBnXmlld', name: 'Domi', gender: 'Female', desc: 'Energetic & Confident' },
    { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella', gender: 'Female', desc: 'Soft & Expressive' },
    { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni', gender: 'Male', desc: 'Deep & Conversational' },
    { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh', gender: 'Male', desc: 'Young & Friendly' },
    { id: 'VR6AewLTigWG4xTox155', name: 'Arnold', gender: 'Male', desc: 'Bold & Crisp' },
];

export const VoiceChangerModal: React.FC<VoiceChangerModalProps> = ({
    isOpen,
    onClose,
    currentPreset,
    onSelectPreset,
    onTransformElevenLabs,
    isConvertingElevenLabs = false,
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData ? usersData[currentUser] : undefined;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(r) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [activeTab, setActiveTab] = useState<'webaudio' | 'elevenlabs'>('webaudio');
    const [selectedElevenVoice, setSelectedElevenVoice] = useState('21m00Tcm4TlvDq8ikWAM');
    const [customApiKey, setCustomApiKey] = useState('');

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center p-4 z-[150] animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-[#0e0e11] border border-white/10 rounded-[2.5rem] w-full max-w-md h-[80vh] overflow-hidden flex flex-col shadow-2xl backdrop-blur-md"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-5 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 animate-pulse" style={{ color: userThemeColor }} />
                        <h3
                            className="text-sm font-black uppercase tracking-tight"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            Voice Mod Studio
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
                        style={{ color: userTextColor }}
                    >
                        <CloseIcon className="w-4 h-4" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="grid grid-cols-2 p-2 bg-black/30 border-b border-white/5">
                    <button
                        onClick={() => setActiveTab('webaudio')}
                        className={`py-2 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
                            activeTab === 'webaudio' ? 'bg-white/15 shadow-md' : 'opacity-50 hover:opacity-100'
                        }`}
                        style={activeTab === 'webaudio' ? { color: userThemeColor, ...stickerTextStyle(0.12) } : undefined}
                    >
                        ⚡ Web Audio (Realtime)
                    </button>
                    {onTransformElevenLabs && (
                        <button
                            onClick={() => setActiveTab('elevenlabs')}
                            className={`py-2 text-xs font-black uppercase tracking-wider rounded-xl transition-all ${
                                activeTab === 'elevenlabs' ? 'bg-white/15 shadow-md' : 'opacity-50 hover:opacity-100'
                            }`}
                            style={activeTab === 'elevenlabs' ? { color: userThemeColor, ...stickerTextStyle(0.12) } : undefined}
                        >
                            ✨ ElevenLabs AI
                        </button>
                    )}
                </div>

                {/* Content area */}
                <div className="flex-1 overflow-y-auto p-4 allow-scroll">
                    {activeTab === 'webaudio' ? (
                        <div className="space-y-3">
                            <p className="text-[11px] opacity-60 px-1" style={stickerTextStyle(0.12)}>
                                Real-time DSP effects for instant voice channels and voice note recording.
                            </p>

                            <div className="grid grid-cols-1 gap-2.5">
                                {VOICE_PRESETS.map((preset) => (
                                    <div
                                        key={preset.id}
                                        onClick={() => {
                                            onSelectPreset(preset.id);
                                            onClose();
                                        }}
                                        className={`p-3.5 rounded-2xl bg-black/20 border transition-all cursor-pointer flex items-center justify-between ${
                                            currentPreset === preset.id
                                                ? 'border-white/40 bg-white/10 shadow-lg scale-[1.01]'
                                                : 'border-white/5 hover:border-white/20'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-2xl">{preset.icon}</span>
                                            <div>
                                                <h4 className="text-xs font-black uppercase" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                                    {preset.label}
                                                </h4>
                                                <p className="text-[10px] opacity-60">{preset.description}</p>
                                            </div>
                                        </div>

                                        {currentPreset === preset.id && (
                                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 border border-green-500/30">
                                                Active
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-[11px] opacity-60 px-1" style={stickerTextStyle(0.12)}>
                                Transform your recorded voice note into an ultra-realistic ElevenLabs AI voice actor.
                            </p>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">Select Voice Model</label>
                                <div className="grid grid-cols-1 gap-2">
                                    {ELEVENLABS_VOICES.map((v) => (
                                        <div
                                            key={v.id}
                                            onClick={() => setSelectedElevenVoice(v.id)}
                                            className={`p-3 rounded-2xl bg-black/20 border transition-all cursor-pointer flex items-center justify-between ${
                                                selectedElevenVoice === v.id
                                                    ? 'border-white/40 bg-white/10 shadow-md'
                                                    : 'border-white/5 hover:border-white/20'
                                            }`}
                                        >
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold text-white">{v.name}</span>
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10 opacity-70">{v.gender}</span>
                                                </div>
                                                <p className="text-[10px] opacity-50 mt-0.5">{v.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-1 pt-2">
                                <label className="text-[10px] font-bold uppercase tracking-wider opacity-70">ElevenLabs API Key (Optional)</label>
                                <input
                                    type="password"
                                    placeholder="sk_... (Leave blank to use app default)"
                                    value={customApiKey}
                                    onChange={(e) => setCustomApiKey(e.target.value)}
                                    className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl py-2.5 px-3.5 text-xs outline-none shadow-inner"
                                    style={{ color: userTextColor }}
                                />
                            </div>

                            <button
                                disabled={isConvertingElevenLabs}
                                onClick={() => {
                                    onTransformElevenLabs?.(selectedElevenVoice, customApiKey.trim() || undefined);
                                }}
                                className="w-full py-3 rounded-2xl font-black uppercase text-xs tracking-wider shadow-lg transition-all transform active:scale-95 border border-white/20 flex items-center justify-center gap-2"
                                style={{ backgroundColor: userThemeColor, color: '#ffffff', ...stickerTextStyle(0.15) }}
                            >
                                {isConvertingElevenLabs ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Converting AI Voice...
                                    </>
                                ) : (
                                    <>
                                        <SparklesIcon className="w-4 h-4" />
                                        Transform Voice Note
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoiceChangerModal;
